import { useCallback, useEffect, useState } from 'react'
import { getSupabase } from '../utils/supabase'
import { isSupabaseConfigured, supabaseConfig } from '../data/supabaseConfig'
import { parseTrafficPayload, type TrafficPayload } from '../utils/traffic'

export type TrafficStatus = 'not-configured' | 'loading' | 'ready' | 'error'

export interface TrafficDataState {
  /** Null until a real response has been received — never placeholder numbers. */
  payload: TrafficPayload | null
  status: TrafficStatus
  error: string | null
  lastUpdated: Date | null
  refresh: () => void
}

const REFRESH_MS = 60_000

/**
 * Reads the aggregated traffic payload from Supabase. There is no sample or
 * fallback data: if the project isn't configured or the query fails, the
 * dashboard says so rather than showing numbers that aren't real.
 */
export function useTrafficData(): TrafficDataState {
  const isConfigured = isSupabaseConfigured()

  const [payload, setPayload] = useState<TrafficPayload | null>(null)
  const [status, setStatus] = useState<TrafficStatus>(
    isConfigured ? 'loading' : 'not-configured'
  )
  const [error, setError] = useState<string | null>(null)
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null)
  const [reloadKey, setReloadKey] = useState(0)

  const refresh = useCallback(() => setReloadKey(key => key + 1), [])

  useEffect(() => {
    const supabase = getSupabase()
    if (!supabase) return

    let cancelled = false

    const load = async () => {
      try {
        const { data, error: queryError } = await supabase.rpc(supabaseConfig.aggregateFunction, {
          p_host: supabaseConfig.monitoredHost,
          p_minutes: supabaseConfig.lookbackMinutes
        })

        if (cancelled) return
        if (queryError) throw new Error(queryError.message)

        setPayload(parseTrafficPayload(data))
        setError(null)
        setLastUpdated(new Date())
        setStatus('ready')
      } catch (cause) {
        if (cancelled) return
        setError(cause instanceof Error ? cause.message : 'Could not reach Supabase')
        setStatus('error')
      }
    }

    load()
    const timer = window.setInterval(load, REFRESH_MS)

    return () => {
      cancelled = true
      window.clearInterval(timer)
    }
  }, [reloadKey])

  return { payload, status, error, lastUpdated, refresh }
}
